import { escapeToSafeCharacters } from "app/components/helpers";
import { API, appInsightsService } from "app/utils/constants";
import { ISearchRequestDto, SearchApiClient, SearchOption, SearchRequestDto } from "generated/search-backend";
import { useMutation, useQuery } from "react-query";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
//getting speech to text token
/** Fetches a speech token only when voice input is requested. */
export const useSpeechToTextToken = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    return useQuery(
        'speechToken',
        async () => {
            return await searchApi.search_GetSpeechToTextToken();
        },
        {
            onSuccess: (res) => {
                sessionStorage.setItem('speech-token', JSON.stringify(res));
            },
            enabled: false,
            retry: false,
            staleTime: 1000 * 60 * 8,
            cacheTime: 1000 * 60 * 8,
        }
    );
};

//get webSearchLinks
export const useGetWebSearchLinks = (term: string) => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    return useQuery(['getWebSearchLinks', term?.trim()], async () => {
        if (term?.trim()) {
            return await searchApi.search_WebSearch(escapeToSafeCharacters(term));
        }
    });
};

//get IntranetSearchLinks
export const useGetIntranetSearchLinks = (term: string) => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    return useQuery(['getIntranetSearchLinks', term?.trim()], async () => {
        if (term?.trim()) {
            return await searchApi.search_SharepointSearch(escapeToSafeCharacters(term));
        }
    });
};
//#endregion
//#region Mutations
export const useSearchData = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    const response = useMutation(({term, searchOption, isAdminSearch, isParentChildSearch, customSearchFields}:{term: string, searchOption: SearchOption, isAdminSearch: boolean, isParentChildSearch: boolean, customSearchFields?: string[]}) => searchApi.search_Suggest2(term, searchOption, isAdminSearch, isParentChildSearch, customSearchFields), {
        onSuccess: () => {
            appInsightsService.logEvent('useSearchData was Succesful');
        }
    });
    return response;
};

// Search
export const useAutoSearch = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    const response = useMutation(
        ({term, customSearchFields}: {term: string, customSearchFields?: string[]}) => searchApi.search_AutoComplete(term, customSearchFields),
        {
            onSuccess: () => {
                appInsightsService.logEvent('useAutoSearch was Succesful');
            }
        }
    );
    return response;
};

export const useSearchResult = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    const requestTypeMutation = useMutation((data: ISearchRequestDto) => searchApi.search_Search(SearchRequestDto.fromJS(data)), {
        onSuccess: () => {
            appInsightsService.logEvent('useSearchResult was Succesful');
        }
    });
    return requestTypeMutation;
};

/**
 * Module: Request Experience Search Customer
 * Custom hook to get list of customers*/
export const useGetCustomersList = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    const response = useMutation(({searchValue, pageSize}:{searchValue?: string, pageSize?: number}) => searchApi.search_Suggest(searchValue, pageSize), {
        onSuccess: () => {
            appInsightsService.logEvent('useGetCustomersList was Succesful');
        }
    });
    return response;
};

/**
 * Mutation to download visual flow
 */
export const useDownloadSearchResults = () => {
    const searchApi : SearchApiClient = new SearchApiClient(baseUrl);
    const mutation = useMutation((data: ISearchRequestDto) => searchApi.search_DownloadSearchResult(SearchRequestDto.fromJS(data)), {
        onSuccess: () => {
            appInsightsService.logEvent('useDownloadSearchResults was Succesful');
        }
    });
    return mutation;
};
//#endregion